import { SMS_ALIAS_LIST_FLOW_NAME } from '@/features/campaigns/v2/Sms/constants';
import { useSmsWorkspace } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetConnectorFlowsQuery, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';

type ConnectorFlowLike = { id: string; name: string; connectorId: string };

const isAliasFlowForConnector = (flow: ConnectorFlowLike, connectorId: string) =>
  flow.connectorId === connectorId && /alias/i.test(flow.name);

const pickAliasFlow = (flows: ConnectorFlowLike[] | undefined, connectorId: string, operationPattern: RegExp) =>
  flows?.find((flow) => isAliasFlowForConnector(flow, connectorId) && operationPattern.test(flow.name));

/**
 * Resolve Alias CRUD flows on the SMS connector.
 * List uses exact name `Alias List`; Insert/Edit/Delete match Alias + operation keywords.
 */
export const useSmsAliasConnector = () => {
  const workspace = useWorkspace();
  const { connectorId } = useSmsWorkspace();

  const {
    data: connectorFlows,
    isLoading: isFlowsLoading,
    isError: isFlowsError,
  } = useGetConnectorFlowsQuery({ workspaceId: workspace.id, queryParams: { showUserJourney: true } }, { skip: !workspace.id });

  const {
    data: connectorInstances,
    isLoading: isInstancesLoading,
    isError: isInstancesError,
  } = useGetConnectorInstancesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });

  const listFlow = connectorFlows?.find((flow) => flow.name === SMS_ALIAS_LIST_FLOW_NAME && flow.connectorId === connectorId);
  const insertFlow = pickAliasFlow(connectorFlows, connectorId, /insert|create|register/i);
  const editFlow = pickAliasFlow(connectorFlows, connectorId, /edit|update/i);
  const deleteFlow = pickAliasFlow(connectorFlows, connectorId, /delete|remove/i);

  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === connectorId)?.id;

  const isLoading = isFlowsLoading || isInstancesLoading;
  const isError = isFlowsError || isInstancesError;

  const isListReady = !!listFlow?.id && !!connectorInstanceId;
  const isInsertReady = !!insertFlow?.id && !!connectorInstanceId;
  const isEditReady = !!editFlow?.id && !!connectorInstanceId;
  const isDeleteReady = !!deleteFlow?.id && !!connectorInstanceId;
  const hasAnyAliasFlow = !!listFlow || !!insertFlow || !!editFlow || !!deleteFlow;

  return {
    listFlowId: listFlow?.id,
    insertFlowId: insertFlow?.id,
    editFlowId: editFlow?.id,
    deleteFlowId: deleteFlow?.id,
    listFlow,
    insertFlow,
    editFlow,
    deleteFlow,
    connectorInstanceId,
    connectorId,
    isLoading,
    isError,
    isListReady,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    missingListFlow: !isLoading && !listFlow,
    missingInsertFlow: !isLoading && !insertFlow,
    missingEditFlow: !isLoading && !editFlow,
    missingDeleteFlow: !isLoading && !deleteFlow,
    missingInstance: !isLoading && hasAnyAliasFlow && !connectorInstanceId,
  };
};
